import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { getCurrentDateTime } from 'pages/utils/commonUtils';
import { PrimaryButton, PrimaryPinkButton, WhiteButton } from "components/common/Button";
import { Model } from "type/type";
import { postModel, putModel, deleteModel, getModelOne } from "api/api";
import Loading from "components/common/Loading";
import { useForm } from "react-hook-form";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .process-content {
    > div {
      max-width: 700px;
    }
    input {
      min-width: 150px;
      max-width: 400px;
      // width: 50%;
    }

    .select-wrap {
      position: relative;
      min-width: 150px;
      max-width: 400px;
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        right: 20px;
        border: 5px solid transparent;
        border-top-color: #313a43;
        transform: scaleY(1.4);
      }
      &::before {
        content: '';
        position: absolute;
        display: block;
        width: 1px;
        height: 95%;
        top: 0;
        right: 48px;
        background-color: #e7e7e7;
        z-index: 0;
      }
    }

    &.event {
      label {
        font-size: 13px;
      }
      &.select-wrap {
        font-size: 14px;
        width: 60%;
      }
    }
  }
  
  .process-content2 {
    > div {
      max-width: 1400px;
    }
  }

  .err-border {
    border: 1px solid red !important;
  }
`;

const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${prop => prop.theme.layer3BgColor};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem;
  gap: 1rem;
  // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
  // border-bottom: 1px solid #666;

  label {
    width: 6rem;
  }
  label, input, select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  textarea {
    resize: none;
    min-height: 10rem;
  }
  input:not([type="radio"]), textarea {
    width: calc(100% - 6rem);
    border: none;
    background-color: #f1f1f1;
    border-radius: 0.25rem;
    &:focus-visible {
      outline: none;
      &:not(:disabled, :read-only) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:read-only {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }
  
  select {
    border: none;
    border-radius: 0.25rem;
    background-color: #f1f1f1;
    
    &:focus-visible {
      outline: none;
      &:not(:disabled) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:disabled {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }

  input[type="radio"] {
    appearance: none;
    width: 1rem;
    height: 1rem;
    border-radius: 0.25rem;
    background-color: #fff;
    &:checked {
      background-color: ${prop => prop.theme.primaryColor};
    }
  }
  &.state-wrap {
    > div {
      align-items: center;
      gap: 0.5rem;
      label {
        width: auto;
      }
    }
  }
  &.as-content {
    span {
      width: calc(100% - 6rem);
      padding: 0.5rem;
      border-bottom: 1px solid #666;
      cursor: default;
      font-size: 14px;
      line-height: 20px;
      word-break: break-all;
      overflow-wrap: break-word;
    }
  }
`;

function AfterServiceProcess() {
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  // 목록에서 전달 받은 값
  const { recorderModelIdx } = useParams();
  const [loading, setLoading] = useState(true);
  
  // useForm
  const { register, formState: { errors, isValid }, trigger } = useForm<any>({
    mode: 'all',
  });

  const currentDateTime = getCurrentDateTime();
  const [recorderModel, setRecorderModel] = useState<Model>({
    idx: recorderModelIdx ? Number(recorderModelIdx) : 0,
    name: '', // 모델 명
    vendor: '', //제조사
    create_date: currentDateTime, //생성일
    channel: 4, // 채널 수
    field_detection: 0, //침입 감지
    line_detection: 0, //경계선 통과 감지
    thermal: 0, //열 감지
    audio_exception: 0, //비정상 오디오 감지
    guard_change: 0, //경계 설정 변화
    sensor_on: 0, //센서 ON
    env_sensor: 0, //환경 센서
    fall_down: 0, //쓰러짐
    loitering: 0, //배회(장기체류)
    smog: 0, //연기/화재 감지
    type: 'NVR', //NVR/DVR 구분
  });

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    const stringFields = new Set([
      "name",
      "vendor",
      "create_date",
      "type",
    ]);
  
    setRecorderModel((prev) => ({
      ...prev,
      [name]: stringFields.has(name) ? value : Number(value),
    }));
  };
  
  const _createRecorderModel = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await postModel(recorderModel);
    if (response.code === 200) {
      alert("녹화기 Model이 등록되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };
  
  const _deleteRecorderModel = async () => {
    const isDel = window.confirm(`${recorderModel.name}을 정말로 삭제하시겠습니까?`);
    if (isDel) {
      const response = await deleteModel(Number(recorderModelIdx));
      if (response.code === 200) {
        alert("녹화기 Model이 삭제되었습니다..");
        navigate(-1);
      } else {
        alert(response.message);
      }
    }
  };

  const _editRecorderModel = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await putModel(recorderModel);
    if (response.code === 200) {
      alert("녹화기 Model이 수정되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };

  // 녹화기 MODEL 가져오기
  const getRecorderModel = useCallback(async () => {
    try {
      setLoading(true); // 🔥 요청 시작 전에 딩 설정
      if (!recorderModelIdx) {
        return setLoading(false);
      }
      const recorderModelRes = await getModelOne(Number(recorderModelIdx));
      if (recorderModelRes.code === 200) {
        setRecorderModel(recorderModelRes.data);
      } else {
        alert(recorderModelRes.message);
      }
  
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoading(false); // 🔥 요청이 끝나면 무조건 로딩 종료
    }
  }, [recorderModelIdx]);

  // 처음 useEffect 실행 및 페이지 변경 시 실행
  useEffect(() => {
    getRecorderModel();
  }, [getRecorderModel]);
  return (
    <>
    {loading ? <Loading /> :
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span></span>
            </p>
          </div>
          <div>
            {!recorderModelIdx ?
              <PrimaryButton type="button" onClick={_createRecorderModel}>저장</PrimaryButton>
            :
              <>
                <PrimaryButton type="button" onClick={_editRecorderModel}>저장</PrimaryButton>
                <PrimaryPinkButton type="button" onClick={_deleteRecorderModel}>삭제</PrimaryPinkButton>
              </>
            }
            <WhiteButton type="button" onClick={() => navigate(-1)}>목록</WhiteButton>
          </div>
        </TitleAndBtnContainer>
        <FlexCol>
          <FlexRow className="process-content">
            <InputWrap>
              <label>Model명</label>
              <input type='text' value={recorderModel.name} className={`${errors.name && 'err-border'}`} placeholder="Model명을 입력해주세요." maxLength={50}
                {...register("name", {
                  required: {
                    value: true,
                    message: 'Model명을 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
            <InputWrap>
              <label>제조사</label>
              <input type='text' value={ recorderModel.vendor } className={`${errors.vendor && 'err-border'}`} placeholder="제조사를 입력해주세요." maxLength={45}
                {...register("vendor", {
                  required: {
                    value: true,
                    message: '제조사를 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>채널 수</label>
              <div className="select-wrap">
                <select value={recorderModel.channel}
                  {...register("channel", {
                    required: true,
                    onChange: handleChange,
                  })}
                >
                  <option value="4">4 CH</option>
                  <option value="8">8 CH</option>
                  <option value="16">16 CH</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>구분</label>
              <div className="select-wrap">
                <select value={recorderModel.type}
                  {...register("type", {
                    required: true,
                    onChange: handleChange,
                  })}
                >
                  <option value="NVR">NVR</option>
                  <option value="DVR">DVR</option>
                </select>
              </div>
            </InputWrap>
          </FlexRow>
        </FlexCol>

        {/* Event 설정 */}
        <FlexCol>
          <FlexRow className="process-content event">
            <InputWrap>
              <label>침입 감지</label>
              <div className="select-wrap">
                <select value={recorderModel.field_detection}
                  {...register("field_detection", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>경계선 통과 감지</label>
              <div className="select-wrap">
                <select value={recorderModel.line_detection}
                  {...register("line_detection", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>비정상 오디오 감지</label>
              <div className="select-wrap">
                <select value={recorderModel.audio_exception}
                  {...register("audio_exception", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>열 감지</label>
              <div className="select-wrap">
                <select value={recorderModel.thermal}
                  {...register("thermal", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content event">
            <InputWrap>
              <label>환경 센서</label>
              <div className="select-wrap">
                <select value={recorderModel.env_sensor}
                  {...register("env_sensor", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>센서 ON</label>
              <div className="select-wrap">
                <select value={recorderModel.sensor_on}
                  {...register("sensor_on", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>경계 설정 변화</label>
              <div className="select-wrap">
                <select value={recorderModel.guard_change}
                  {...register("guard_change", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>쓰러짐</label>
              <div className="select-wrap">
                <select value={recorderModel.fall_down}
                  {...register("fall_down", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content event">
            <InputWrap>
              <label>배회(장기체류)</label>
              <div className="select-wrap">
                <select value={recorderModel.loitering}
                  {...register("loitering", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap>
              <label>연기/화재 감지</label>
              <div className="select-wrap">
                <select value={recorderModel.smog}
                  {...register("smog", {
                    required: false,
                    onChange: handleChange,
                  })}
                >
                  <option value="0">이벤트 없음</option>
                  <option value="1">이벤트 사용</option>
                </select>
              </div>
            </InputWrap>
            <InputWrap></InputWrap>
            <InputWrap></InputWrap>
          </FlexRow>
        </FlexCol>
      </Container>
    }
    </>
  );
}

export default AfterServiceProcess;